import { ReportOutput } from '@eppendorf/vnls-reporting-service-utils';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { ConfirmDeleteDialog } from '$components/confirm-delete-dialog/confirm-delete-dialog';
import { ErrorDeleteRetryDialog } from '$components/confirm-delete-dialog/error-delete-retry-dialog';
import { SuccessDeleteDialog } from '$components/confirm-delete-dialog/success-delete-dialog';
import { useDeleteReport } from '$features/report-hub/report-hub.api';

function useDeleteReportHandler() {
  const { mutateAsync } = useDeleteReport();
  return useCallback(
    async (report: ReportOutput | undefined) => {
      if (!report) return;
      await mutateAsync(report.id);
    },
    [mutateAsync],
  );
}

export interface ConfirmDeleteReportWrapperProps {
  selectedReport?: ReportOutput;
  onOpenChange: (open: boolean) => void;
  isDeleteConfirmOpen: boolean;
  onDeleteSuccess?: () => void;
}

export function DeleteReportConfirmWrapper({
  isDeleteConfirmOpen = false,
  onOpenChange,
  selectedReport,
  onDeleteSuccess,
}: ConfirmDeleteReportWrapperProps) {
  const [isErrorDeleteReportDialogOpen, setIsErrorDeleteReportDialogOpen] =
    useState(false);
  const [isSuccessDeleteReportDialogOpen, setIsSuccessDeleteReportDialogOpen] =
    useState(false);

  const deleteReport = useDeleteReportHandler();

  async function onConfirmDeleteFunction(report: ReportOutput | undefined) {
    try {
      await deleteReport(report);
    } catch (error) {
      onOpenChange(false);
      setIsErrorDeleteReportDialogOpen(true);
      return;
    }

    onOpenChange(false);
    setIsSuccessDeleteReportDialogOpen(true);
  }

  return (
    <>
      <ConfirmDeleteDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => onOpenChange(false)}
        onConfirmDelete={() => onConfirmDeleteFunction(selectedReport)}
        title={t('reportHub.deleteConfirm.title')}
        description={
          <div className="text-align-left">
            <div>{t('reportHub.deleteConfirm.prompt')}</div>
            <div>{t('reportHub.deleteConfirm.description')}</div>
          </div>
        }
      />
      <ErrorDeleteRetryDialog
        isOpen={isErrorDeleteReportDialogOpen}
        onClose={() => setIsErrorDeleteReportDialogOpen(false)}
        onTryAgain={() => onConfirmDeleteFunction(selectedReport)}
        title={t('errors.errorOccurred')}
        message={t('errors.unexpected')}
      />
      <SuccessDeleteDialog
        isOpen={isSuccessDeleteReportDialogOpen}
        onClose={() => {
          setIsSuccessDeleteReportDialogOpen(false);
          onDeleteSuccess?.();
        }}
        title={t('reportHub.deleteConfirm.deletedTitle')}
        description={`${t('reportHub.deleteConfirm.message')}`}
      />
    </>
  );
}
